/**
 * MentionMatchedBusinessCard Component
 *
 * Displays matched business information for a mention
 * Shared component used by both MentionReviewDetailPage and BusinessMentionCard
 */
import React from 'react';
import { Card, Stack, Group, Text, Anchor, Alert } from '@mantine/core';
import { IconBuilding, IconMapPin, IconPhone, IconAlertCircle } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { buildBusinessDetailRoute } from '@/presentation/routing/routes';

export interface MentionMatchedBusinessCardProps {
  /**
   * Business ID (GUID string, optional)
   */
  businessId: string | null;

  /**
   * Business name (optional)
   */
  businessName: string | null;

  /**
   * Business address (optional)
   */
  businessAddress: string | null;

  /**
   * Business phone (optional)
   */
  businessPhone: string | null;

  /**
   * Whether to show the card border
   */
  withBorder?: boolean;

  /**
   * Card padding
   */
  padding?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Card shadow
   */
  shadow?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Whether to show the header icon and label
   */
  showHeader?: boolean;
}

/**
 * MentionMatchedBusinessCard - Reusable matched business display
 */
export const MentionMatchedBusinessCard: React.FC<MentionMatchedBusinessCardProps> = ({
  businessId,
  businessName,
  businessAddress,
  businessPhone,
  withBorder = true,
  padding = 'md',
  shadow = 'sm',
  showHeader = true
}): React.ReactElement => {
  // If no business matched, show alert
  if (!businessName) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="yellow">
        No business matched to this mention
      </Alert>
    );
  }

  return (
    <>
      {showHeader && (
        <Group gap="xs" mb="xs">
          <IconBuilding size={20} />
          <Text size="md" fw={500}>
            Matched Business
          </Text>
        </Group>
      )}
      <Card withBorder={withBorder} padding={padding} shadow={shadow}>
        <Stack gap="xs">
          {businessId ? (
            <Anchor component={Link} to={buildBusinessDetailRoute(businessId)} size="sm" fw={500}>
              {businessName}
            </Anchor>
          ) : (
            <Text size="sm" fw={500}>
              {businessName}
            </Text>
          )}
          {businessAddress && (
            <Group gap="xs">
              <IconMapPin size={16} />
              <Text size="sm" c="dimmed">
                {businessAddress}
              </Text>
            </Group>
          )}
          {businessPhone && (
            <Group gap="xs">
              <IconPhone size={16} />
              <Text size="sm" c="dimmed">
                {businessPhone}
              </Text>
            </Group>
          )}
        </Stack>
      </Card>
    </>
  );
};
